import {
  Manager,
  ManagerPage,
  ManagerPageQuery,
  ManagerRequest,
  ManagerUpdate,
} from "../types/manager.definition";
import {
  assignManagerFacility,
  assignManagerToFacility,
  createManager,
  disableManager,
  getAllManagers,
  getManagerAvailabilities,
  getManagersPage,
  getManagerById,
  restoreManager,
  unassignManagerFacility,
  updateManager,
} from "../webclients/manager.client";

/** Results shown by a manager search (assign modal, onboarding). */
const MANAGER_SEARCH_RESULTS = 20;

export const managerApi = {
  getManagerById: async (managerId: string) => {
    const response = await getManagerById(managerId);
    return response.data;
  },
  updateManager: async (managerId: string, data: ManagerUpdate) => {
    const response = await updateManager(managerId, data);
    return response.data;
  },
  getManagerAvailabilities: async (managerId: string) => {
    const response = await getManagerAvailabilities(managerId);
    return response.data;
  },
  createManager: async (data: ManagerRequest) => {
    const response = await createManager(data);
    return response.data;
  },
  getAllManagers: async (): Promise<Manager[]> => {
    const response = await getAllManagers();
    return response.data;
  },
  /** MED-1635: the managers list page — paged, searched and filtered by the server. */
  getManagersPage: async (query: ManagerPageQuery): Promise<ManagerPage> => {
    const response = await getManagersPage(query);
    return response.data;
  },
  assignManagerToFacility: async (
    facilityId: number,
    managerId: number
  ): Promise<Manager> => {
    const response = await assignManagerToFacility(facilityId, managerId);
    return response.data;
  },

  // MED-1470/MED-1472: the four row/detail actions. Each is a real server round-trip —
  // the list and the detail page used to mutate Redux (or log to the console) only.
  assignFacility: async (
    managerId: number,
    facilityId: number
  ): Promise<Manager> => {
    const response = await assignManagerFacility(managerId, facilityId);
    return response.data;
  },
  unassignFacility: async (managerId: number): Promise<Manager> => {
    const response = await unassignManagerFacility(managerId);
    return response.data;
  },
  /** Soft-disables the account; the manager and their data are never erased. */
  disableManager: async (managerId: number): Promise<void> => {
    await disableManager(managerId);
  },
  restoreManager: async (managerId: number): Promise<Manager> => {
    const response = await restoreManager(managerId);
    return response.data;
  },

  // MED-1635: searched on the server (first name, last name, email, phone, login id).
  // It used to filter GET /managers in the browser, which the API caps at its first
  // page of 20, so any manager past the 20th could never be found.
  searchManagers: async (keyword: string): Promise<Manager[]> => {
    const response = await getManagersPage({
      page: 0,
      size: MANAGER_SEARCH_RESULTS,
      sort: "user.lastName,asc",
      search: keyword.trim(),
    });
    return (response.data as ManagerPage).content;
  },
};

export default managerApi;
